import {Component, Input, OnChanges} from "@angular/core";
import {SessionModel} from "../../orchestration/session.model";
import {PossessivePipe} from "../../string-utils/possessive.pipe";
import {ButtonProminence} from "../../buttons/button/button";
import {NavigationService} from "../../orchestration/navigation.service";
import {OrderingContextIntention} from "../../ordering-context/ordering-context";
import {OrderModel} from "../order.model";
import {OrderingContextModel} from "../../ordering-context/ordering-context.model";
import {RecentOrderData} from "../recent-order.service";
import {PaymentType} from "../../payment/payment";
import {OrderItemData, PaymentSurchargeData} from "../order-initiation.service";
import {OrderType} from "../order";

export class TransferRequestDetails {
    constructor(public transferRequestId: string, public transferRequestName: string, public items: OrderItemData[], public subTotal: number) {
    }

    public get name() {
        return this.transferRequestId ? new PossessivePipe().transform(this.transferRequestName) : undefined;
    }

    public get heading() {
        return this.transferRequestId ? "woa.heading.order.summary.items.transferred" : "woa.heading.order.summary.items.local";
    }

    public get subtotalLabel() {
        return this.transferRequestId ? "woa.label.order.summary.subtotal.transferred" : "woa.label.order.summary.subtotal.local";
    }
}

@Component({
    selector: "woa-order-details-panel",
    templateUrl: "./order-details-panel.component.html"
})

export class OrderDetailsPanelComponent implements OnChanges {
    OrderType = OrderType;
    PaymentType = PaymentType;
    ButtonProminence = ButtonProminence;
    OrderingContextIntention = OrderingContextIntention;

    @Input() recentOrder: RecentOrderData;
    @Input() showFulfilmentDetails: boolean = true;
    @Input() showLocationDetails: boolean = true;
    @Input() showOrderDetails: boolean = true;
    @Input() showPayment: boolean = true;

    memberAvailableRedemptionPoints: number;

    transferRequestDetails: TransferRequestDetails[] = [];
    totalTransfers: number;

    constructor(public sessionModel: SessionModel,
                public orderingContextModel: OrderingContextModel,
                public orderModel: OrderModel,
                private navigationService: NavigationService) {
    }

    get totalPayment(): number {
        return this.recentOrder.orderPlacement.paymentAuthorisation.payWithPoints ? this.recentOrder.initiatedOrder.orderTotal
            : this.recentOrder.initiatedOrder.getOrderOrderTotalWithPaymentSurcharge(PaymentSurchargeData.CREDIT_CARD_TYPE);
    }

    get paymentMethod(): string {
        return this.recentOrder.orderPlacement.paymentAuthorisation.paymentType === PaymentType.POINTS ? "woa.label.order.pointsPayment"
            : this.recentOrder.orderPlacement.paymentAuthorisation.paymentType === 1 || this.recentOrder.orderPlacement.paymentAuthorisation.paymentType === PaymentType.SAVED_CREDIT_CARD || this.recentOrder.orderPlacement.paymentAuthorisation.paymentType === PaymentType.NEW_CREDIT_CARD ? (this.recentOrder.orderPlacement.paymentAuthorisation.paymentCardLast4 ? "woa.label.order.creditCardPayment" : "woa.label.order.unknownCreditCardPayment")
                : this.recentOrder.orderPlacement.paymentAuthorisation.paymentType === PaymentType.GOOGLE_PAY ? "woa.label.order.googlePayPayment"
                    : this.recentOrder.orderPlacement.paymentAuthorisation.paymentType === PaymentType.APPLE_PAY ? "woa.label.order.applePayPayment"
                        : this.recentOrder.orderPlacement.paymentAuthorisation.payWithPoints ? "woa.label.order.pointsPayment"
                            : this.recentOrder.orderPlacement.paymentAuthorisation.paymentCardLast4 ? "woa.label.order.creditCardPayment"
                                : "woa.label.order.unknownCreditCardPayment";
    }

    get orderTimeLabel(): string {
        return this.recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE ? (this.recentOrder.placedOrder.requestId && this.recentOrder.placedOrder.placedDateTime ? "woa.label.order.details.tableOrderTime" : undefined)
            : !this.orderingContextModel.canShowETA ? (this.recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.label.order.details.asapPickupTime" : "woa.label.order.details.asapDeliveryTime")
                : this.recentOrder.initiatedOrder.orderLaterDateTime ? (this.recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.label.order.details.scheduledPickupTime" : "woa.label.order.details.scheduledDeliveryTime")
                    : this.recentOrder.placedOrder.requestId ? (this.recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.label.order.details.estimatedPickupTime" : "woa.label.order.details.estimatedDeliveryTime")
                        : undefined;
    }

    get orderTime(): string {
        return this.recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE ? this.orderModel.formatPlacedDateTime(this.recentOrder.placedOrder)
            : this.recentOrder.initiatedOrder.orderLaterDateTime ? (this.recentOrder.placedOrder && this.recentOrder.placedOrder.estimatedCompletionLocalDateTime ? this.orderModel.formatEstimatedCompletionDateTime(this.recentOrder.placedOrder) : this.orderModel.formatRequiredDateTime(this.recentOrder.initiatedOrder))
                : this.recentOrder.placedOrder.requestId ? this.recentOrder.placedOrder.estimatedCompletionTime
                    : undefined;
    }

    get showQrCode(): boolean {
        return this.recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD;
    }

    ngOnChanges(): void {
        if (this.recentOrder) {
            if (this.recentOrder.orderPlacement.paymentAuthorisation.payWithPoints) {
                this.memberAvailableRedemptionPoints = this.recentOrder.initiatedOrder.memberAvailableRedemptionPoints;
            }

            this.totalTransfers = this.recentOrder.orderInitiation.totalTransfers;
            this.transferRequestDetails = this.recentOrder.orderInitiation.items
                .filter((item: OrderItemData, index: number, items: OrderItemData[]) => items.findIndex((r: OrderItemData) => r.transferRequestId === item.transferRequestId) === index)
                .sort((a: OrderItemData, b: OrderItemData) => (a.transferRequestName || "").localeCompare(b.transferRequestName || ""))
                .map((item: OrderItemData) => new TransferRequestDetails(
                    item.transferRequestId, item.transferRequestName,
                    this.recentOrder.orderInitiation.getItemsForTransferRequest(item.transferRequestId),
                    this.recentOrder.orderInitiation.getSubtotalForTransferRequest(item.transferRequestId)));
        }
    }

    getRequestHeading(requestDetails: TransferRequestDetails): string {
        return this.transferRequestDetails.length === 1 ? "woa.heading.order.summary.order" : requestDetails.heading;
    }

    requestInvoice(): void {
        // noinspection JSIgnoredPromiseFromCall
        this.navigationService.createPage("woa-invoice-request-page", {
            recentOrder: this.recentOrder
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/order/order-details-panel/order-details-panel.component.ts